/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { NegotiableQuoteHistoryEntry, NegotiableQuoteHistoryEntryChangeType  } from '../models/negotiable-quote-model';

/**
 * Transforms a history entry from the API format to the model format
 * @param historyEntry - Raw history entry from API
 * @returns Transformed history entry
 */
export function transformHistoryEntry(
  historyEntry: any
): NegotiableQuoteHistoryEntry {
  return {
    uid: historyEntry.uid,
    createdAt: historyEntry.created_at,
    author: {
      firstname: historyEntry.author.firstname,
      lastname: historyEntry.author.lastname,
    },
    changeType: historyEntry.change_type as NegotiableQuoteHistoryEntryChangeType,
    changes: {
      commentAdded: historyEntry.changes?.comment_added && {
        comment: historyEntry.changes.comment_added.comment,
      },
      customChanges: historyEntry.changes?.custom_changes && {
        new_value: historyEntry.changes.custom_changes.new_value,
        old_value: historyEntry.changes.custom_changes.old_value,
        title: historyEntry.changes.custom_changes.title,
      },
      expiration: historyEntry.changes?.expiration && {
        newExpiration: historyEntry.changes.expiration.new_expiration,
        oldExpiration: historyEntry.changes.expiration.old_expiration,
      },
      productsRemoved: historyEntry.changes?.products_removed && {
        productsRemovedFromCatalog:
          historyEntry.changes.products_removed.products_removed_from_catalog ||
          [],
        productsRemovedFromQuote:
          historyEntry.changes.products_removed.products_removed_from_quote ||
          [],
      },
      statuses: historyEntry.changes?.statuses && {
        changes:
          historyEntry.changes.statuses.changes?.map((change: any) => ({
            newStatus: change?.new_status,
            oldStatus: change?.old_status,
          })) || [],
      },
      total:
        historyEntry.changes?.total &&
        historyEntry.changes.total.new_price &&
        historyEntry.changes.total.old_price && {
          newPrice: {
            value: historyEntry.changes.total.new_price.value,
            currency: historyEntry.changes.total.new_price.currency,
          },
          oldPrice: {
            value: historyEntry.changes.total.old_price.value,
            currency: historyEntry.changes.total.old_price.currency,
          },
        },
    },
  };
}

/**
 * Transforms an array of history entries from the API format to the model format
 * @param historyEntries - Array of raw history entries from API
 * @returns Array of transformed history entries
 */
export function transformHistory(
  historyEntries: any[] | null | undefined
): NegotiableQuoteHistoryEntry[] | undefined {
  if (!historyEntries) {
    return undefined;
  }

  return historyEntries.map(transformHistoryEntry);
}

